import { create } from 'zustand';

export type QuizPhase = 'question' | 'feedback';

interface QuizUiState {
  sessionId: string | null;
  phase: QuizPhase;
  /** Id спроби, фідбек якої зараз на екрані. */
  activeAttemptId: string | null;
  activeItemId: string | null;
  /** Момент показу питання — з нього рахуємо і таймер, і responseMs. */
  questionStartedAt: number;
  hintsUsed: number;
  hints: string[];
  /**
   * Відповідь уже пішла в Dexie, але фідбек ще не показаний.
   * Прапорець тримає екран на місці, поки триває запис: без нього
   * позиція (яка рахується з фактів у базі) встигає перескочити далі,
   * і фідбек останнього питання проковтується.
   */
  submitting: boolean;
  /** Лічильник, що запускає анімацію тряски при помилці. */
  shakeNonce: number;
  burstNonce: number;

  beginSubmit: () => void;
  beginSession: (sessionId: string) => void;
  beginQuestion: (itemId: string, now?: number) => void;
  showFeedback: (attemptId: string, itemId: string) => void;
  /** Закрити фідбек і повернутись до питань. Яке саме буде наступним — вирішує екран. */
  finishFeedback: () => void;
  addHint: (text: string) => void;
  bumpShake: () => void;
  bumpBurst: () => void;
  reset: () => void;
}

/**
 * Zustand тримає ТІЛЬКИ ефемерний UI-стан ігрового екрана.
 * Ні балів, ні серій, ні результатів тут немає і не буде:
 * факти живуть у Dexie, похідні числа — на сервері.
 */
export const useQuizStore = create<QuizUiState>((set) => ({
  sessionId: null,
  phase: 'question',
  activeAttemptId: null,
  activeItemId: null,
  questionStartedAt: Date.now(),
  hintsUsed: 0,
  hints: [],
  submitting: false,
  shakeNonce: 0,
  burstNonce: 0,

  beginSubmit: () => set({ submitting: true }),

  beginSession: (sessionId) =>
    set({
      sessionId,
      phase: 'question',
      activeAttemptId: null,
      activeItemId: null,
      // Скидаємо відлік: інакше нова сесія успадкувала б час попередньої.
      questionStartedAt: Date.now(),
      hintsUsed: 0,
      hints: [],
      submitting: false,
    }),

  beginQuestion: (itemId, now = Date.now()) =>
    set({
      phase: 'question',
      activeItemId: itemId,
      activeAttemptId: null,
      questionStartedAt: now,
      hintsUsed: 0,
      hints: [],
      submitting: false,
    }),

  showFeedback: (attemptId, itemId) =>
    set({ phase: 'feedback', activeAttemptId: attemptId, activeItemId: itemId, submitting: false }),

  finishFeedback: () =>
    set({ phase: 'question', activeAttemptId: null, hintsUsed: 0, hints: [], submitting: false }),

  addHint: (text) =>
    set((state) => ({ hints: [...state.hints, text], hintsUsed: state.hintsUsed + 1 })),

  bumpShake: () => set((state) => ({ shakeNonce: state.shakeNonce + 1 })),
  bumpBurst: () => set((state) => ({ burstNonce: state.burstNonce + 1 })),

  reset: () =>
    set({
      sessionId: null,
      phase: 'question',
      activeAttemptId: null,
      activeItemId: null,
      hintsUsed: 0,
      hints: [],
      submitting: false,
    }),
}));
